<template>
  <PageHeader
    title="Pricing Catalog Management"
    subtitle="Control the construction pricing used for conventional solution estimates"
  />

  <div class="content-container">
    <div class="bg-white pa-30px br-10px mb-20px">
      <h3 class="mt-0">Manage Construction Pricing Database</h3>
      <p class="text-muted">
        Upload a custom CSV file to override the default pricing catalog used for conventional solution estimates. The
        CSV can have any columns you want - the AI will interpret the data.
      </p>
    </div>

    <div class="bg-white pa-30px br-10px">
      <div class="action-section">
        <h4>Download Current Catalog</h4>
        <p class="text-muted">Download the currently active pricing catalog to review or edit.</p>
        <AppButton variant="outlined" :disabled="loading" @click="handleDownload">
          <span v-if="loading">Downloading...</span>
          <template v-else><AppIcon type="download" size="18" class="mr-5px" /> Download CSV</template>
        </AppButton>
      </div>

      <div class="divider"></div>

      <div class="action-section">
        <h4>Upload Custom Catalog</h4>
        <p class="text-muted">Upload your own pricing CSV to replace the default catalog.</p>
        <div class="upload-section">
          <input ref="fileInput" type="file" accept=".csv" @change="handleFileSelect" style="display: none" />
          <AppButton variant="outlined" :disabled="loading" @click="fileInput.click()">
            <AppIcon type="upload" size="18" class="mr-5px" /> Choose CSV File
          </AppButton>
          <span v-if="selectedFile" class="file-name">{{ selectedFile.name }}</span>
        </div>
        <AppButton v-if="selectedFile" :disabled="loading" @click="handleUpload">
          <span v-if="loading">Uploading...</span>
          <template v-else><AppIcon type="check" size="18" class="mr-5px" /> Upload & Apply</template>
        </AppButton>
      </div>

      <div class="divider"></div>

      <div class="action-section">
        <h4>Reset to Default</h4>
        <p class="text-muted">Remove your custom catalog and revert to the system default.</p>
        <AppButton variant="danger" :disabled="loading" @click="handleReset">
          <span v-if="loading">Resetting...</span>
          <template v-else><AppIcon type="reset" size="18" class="mr-5px" /> Reset to Default</template>
        </AppButton>
      </div>
    </div>

    <div v-if="message" :class="['message', messageType]" role="status">
      {{ message }}
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { AppButton, AppIcon } from "@/components/ui";
import PageHeader from "@/components/PageHeader.vue";
import { usePricingCatalog } from "@/composables/usePricingCatalog";

const fileInput = ref(null);

const {
  selectedFile,
  loading,
  message,
  messageType,
  downloadCatalog,
  uploadCatalog,
  resetToDefault,
} = usePricingCatalog();

const handleFileSelect = (event) => {
  const file = event.target.files[0];
  if (file) {
    if (!file.name.endsWith(".csv")) {
      message.value = "Please select a CSV file";
      messageType.value = "error";
      return;
    }
    selectedFile.value = file;
    message.value = "";
  }
};

const handleDownload = async () => {
  await downloadCatalog();
};

const handleUpload = async () => {
  if (!selectedFile.value) return;

  const success = await uploadCatalog(selectedFile.value);
  if (success && fileInput.value) {
    fileInput.value.value = "";
  }
};

const handleReset = async () => {
  if (
    !confirm("Are you sure you want to reset to the default pricing catalog? This will delete your custom catalog.")
  ) {
    return;
  }

  await resetToDefault();
};
</script>

<style lang="scss" scoped>
@use "sass:color";

.content-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 30px;

  @media (max-width: 768px) {
    padding: 16px;
  }
}

.action-section {
  h4 {
    margin-bottom: 4px;
  }

  p {
    margin-bottom: 16px;
  }
}

.divider {
  height: 1px;
  background: $color-border;
  margin: 28px 0;
}

.upload-section {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}

.file-name {
  color: $color-text-muted;
  font-size: 14px;
}

.message {
  margin-top: 20px;
  padding: 14px 16px;
  border: 1px solid;
  border-radius: 10px;
  color: $color-ink;
  text-align: center;

  &.success {
    background: $color-green-1;
    border-color: rgba($color-green, 0.4);
  }

  &.error {
    background: color.mix($color-white, $color-error, 94%);
    border-color: rgba($color-error, 0.35);
    color: $color-error;
  }
}
</style>
